import React, { useState } from 'react';
import {
  Database, Play, Eye, CheckCircle2, AlertCircle, Loader2, Info, ShieldCheck,
} from 'lucide-react';
import { api } from '../../services/api';

function formatBytes(bytes) {
  if (!bytes || bytes < 1024) return `${bytes || 0} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function Card({ title, description, safe, danger, icon: Icon, children }) {
  const border = danger ? 'border-rose-200' : safe ? 'border-emerald-200' : 'border-slate-100';
  const bg = danger ? 'bg-rose-50' : safe ? 'bg-emerald-50' : 'bg-white';
  const iconBg = danger ? 'bg-rose-100 text-rose-600' : safe ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-600';
  return (
    <div className={`rounded-xl border ${border} ${bg} p-5`}>
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-9 h-9 rounded-lg ${iconBg} flex items-center justify-center shrink-0`}>
          <Icon size={18} />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-sm text-slate-800">{title}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{description}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function ResultPanel({ result, loading }) {
  if (loading) {
    return (
      <div className="mt-3 flex items-center gap-2 text-xs text-slate-500 bg-white border border-slate-100 rounded-lg p-3">
        <Loader2 size={14} className="animate-spin" /> Wird ausgeführt…
      </div>
    );
  }
  if (!result) return null;

  const isDry = !!result.dry_run;
  return (
    <div
      className={`mt-3 rounded-lg p-3 border text-xs ${
        isDry ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'
      }`}
      data-testid="maintenance-result"
    >
      <div className="flex items-center gap-1.5 font-bold mb-2">
        {isDry ? (
          <>
            <Eye size={14} className="text-amber-600" />
            <span className="text-amber-800">Vorschau (Dry-Run) – nichts wurde geändert</span>
          </>
        ) : (
          <>
            <CheckCircle2 size={14} className="text-emerald-600" />
            <span className="text-emerald-800">Bereinigung erfolgreich</span>
          </>
        )}
      </div>
      <div className="text-slate-700 mb-2">{result.message}</div>
      <div className="grid grid-cols-2 gap-2">
        {'before' in result && (
          <div className="bg-white/60 rounded p-2">
            <p className="text-[10px] text-slate-500 uppercase">Betroffen</p>
            <p className="text-base font-bold text-slate-800">{result.before}</p>
          </div>
        )}
        {'modified' in result && (
          <div className="bg-white/60 rounded p-2">
            <p className="text-[10px] text-slate-500 uppercase">Bereinigt</p>
            <p className="text-base font-bold text-slate-800">{result.modified}</p>
          </div>
        )}
        {'remaining' in result && (
          <div className="bg-white/60 rounded p-2">
            <p className="text-[10px] text-slate-500 uppercase">Verbleibend</p>
            <p className="text-base font-bold text-slate-800">{result.remaining}</p>
          </div>
        )}
        {'estimated_total_bytes_freed' in result && result.estimated_total_bytes_freed > 0 && (
          <div className="bg-white/60 rounded p-2">
            <p className="text-[10px] text-slate-500 uppercase">Größe freigemacht (geschätzt)</p>
            <p className="text-base font-bold text-slate-800">{formatBytes(result.estimated_total_bytes_freed)}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export function AdminMaintenanceTab() {
  const [dryLoading, setDryLoading] = useState(false);
  const [runLoading, setRunLoading] = useState(false);
  const [dryResult, setDryResult] = useState(null);
  const [runResult, setRunResult] = useState(null);
  const [error, setError] = useState('');

  const runMigration = async (dryRun) => {
    setError('');
    if (dryRun) {
      setDryLoading(true);
      setDryResult(null);
    } else {
      // Confirmation prompt only for the real run
      const ok = window.confirm(
        'Bereinigung jetzt wirklich ausführen?\n\n' +
        'Es werden nur die eingebetteten Fahrzeug-Fotos aus alten Angeboten entfernt. ' +
        'Die Originalbilder bei den Fahrern bleiben unberührt.\n\n' +
        'Dieser Vorgang ist idempotent und sicher.'
      );
      if (!ok) return;
      setRunLoading(true);
      setRunResult(null);
    }
    try {
      const res = await api.post(
        `/admin/migrate/strip-offer-vehicle-documents${dryRun ? '?dry_run=true' : ''}`
      );
      if (dryRun) setDryResult(res.data);
      else setRunResult(res.data);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      const body = e.response?.data;
      const url = e.config?.url || e.request?.responseURL || '';
      let msg;
      if (status === 404) {
        msg = `Endpoint noch nicht deployed (HTTP 404). Ist der letzte Redeploy schon abgeschlossen? URL: ${url}`;
      } else if (status === 401 || status === 403) {
        msg = `Nicht autorisiert (HTTP ${status}). Bitte als Admin neu einloggen.`;
      } else if (status) {
        msg = `HTTP ${status}: ${detail || (typeof body === 'string' ? body : JSON.stringify(body))}`;
      } else if (e.message) {
        msg = `Netzwerkfehler: ${e.message}`;
      } else {
        msg = 'Migration fehlgeschlagen. Bitte Logs prüfen.';
      }
      setError(msg);
    } finally {
      setDryLoading(false);
      setRunLoading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-maintenance-tab">
      {/* Intro banner */}
      <div className="bg-slate-800 rounded-xl p-5 text-white">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={18} className="text-emerald-400" />
          <h2 className="font-bold text-base">Datenbank-Wartung</h2>
        </div>
        <p className="text-sm text-slate-300">
          Einmalige technische Bereinigungen, die die Datenbank schlanker und die App schneller machen.
          Kundendaten, Buchungen, Zahlungen und Bewertungen werden nicht berührt.
        </p>
      </div>

      {/* Migration: Strip offer vehicle documents */}
      <Card
        icon={Database}
        title="Angebote bereinigen (vehicle.documents)"
        description="Entfernt eingebettete Fahrzeugfotos/Versicherung/Zulassung aus alten Angeboten. Die Originalbilder bleiben bei den Fahrzeugen der Fahrer erhalten."
      >
        <div className="bg-white/70 rounded-lg p-3 border border-slate-100 text-xs text-slate-600 mb-3 flex items-start gap-2">
          <Info size={12} className="mt-0.5 text-slate-400 shrink-0" />
          <div>
            <p className="font-medium text-slate-700 mb-1">Was passiert genau?</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Jedes Angebot in <code className="bg-slate-100 px-1 rounded">offers</code> verliert das Feld <code className="bg-slate-100 px-1 rounded">vehicle.documents</code></li>
              <li>Fahrer, Fahrzeuge, Kunden, Buchungen und Zahlungen sind nicht betroffen</li>
              <li>Der Vorgang ist idempotent – kann mehrfach ohne Nebenwirkung ausgeführt werden</li>
              <li>Meine Transfers-Endpoint antwortet danach schneller (kleinere Payload)</li>
            </ul>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => runMigration(true)}
            disabled={dryLoading || runLoading}
            className="flex items-center gap-1.5 px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-slate-700"
            data-testid="maintenance-dryrun-btn"
          >
            {dryLoading ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
            Vorschau (Dry-Run)
          </button>
          <button
            onClick={() => runMigration(false)}
            disabled={dryLoading || runLoading}
            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-bold text-white"
            data-testid="maintenance-run-btn"
          >
            {runLoading ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
            Bereinigung starten
          </button>
        </div>

        <ResultPanel result={dryResult} loading={dryLoading} />
        <ResultPanel result={runResult} loading={runLoading} />
        {error && !dryLoading && !runLoading && (
          <div className="mt-3 flex items-start gap-2 text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="maintenance-error">
            <AlertCircle size={14} className="mt-0.5 shrink-0 text-rose-600" />
            <div className="break-all">{error}</div>
          </div>
        )}
      </Card>
    </div>
  );
}
